import React, { createContext, useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../services/api';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [config, setConfig] = useState(null);
  const [languages, setLanguages] = useState([]); // Initialize as empty array
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [fontSize, setFontSize] = useState(16);
  const [highContrast, setHighContrast] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSiteConfig();
    loadLanguages();
    
    // Load preferences from localStorage
    const savedLang = localStorage.getItem('language') || 'en';
    const savedFontSize = parseInt(localStorage.getItem('fontSize')) || 16;
    const savedContrast = localStorage.getItem('highContrast') === 'true';
    
    setCurrentLanguage(savedLang);
    setFontSize(savedFontSize);
    setHighContrast(savedContrast);
    i18n.changeLanguage(savedLang);
    
    document.documentElement.style.fontSize = `${savedFontSize}px`;
    if (savedContrast) {
      document.body.classList.add('high-contrast');
    }
  }, [i18n]);

  const loadSiteConfig = async () => {
    try {
      const response = await api.get('/config/active/');
      setConfig(response.data);
    } catch (error) {
      console.error('Error loading site config:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLanguages = async () => {
    try {
      const response = await api.get('/languages/');
      // Ensure we always set an array
      if (Array.isArray(response.data)) {
        setLanguages(response.data);
      } else if (response.data.results && Array.isArray(response.data.results)) {
        setLanguages(response.data.results);
      } else {
        setLanguages([]);
      }
    } catch (error) {
      console.error('Error loading languages:', error);
      setLanguages([]); // Set empty array on error
    }
  };

  const changeLanguage = (lang) => {
    setCurrentLanguage(lang);
    i18n.changeLanguage(lang);
    localStorage.setItem('language', lang);
  };

  const changeFontSize = (size) => {
    setFontSize(size);
    document.documentElement.style.fontSize = `${size}px`;
    localStorage.setItem('fontSize', size);
  };

  const toggleHighContrast = () => {
    const newValue = !highContrast;
    setHighContrast(newValue);
    if (newValue) {
      document.body.classList.add('high-contrast');
    } else {
      document.body.classList.remove('high-contrast');
    }
    localStorage.setItem('highContrast', newValue);
  };

  const value = {
    config,
    languages,
    currentLanguage,
    changeLanguage,
    fontSize,
    changeFontSize,
    highContrast,
    toggleHighContrast,
    loading
  };

  return <SiteContext.Provider value={value}>{children}</SiteContext.Provider>;
};
